import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Icon, Input } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { TagChip } from './TagChip.js';
import { type DecryptedTag } from './tagCache.js';

interface Props {
  open: boolean;
  // The leaf album of every selected photo (the bulk action lives inside one
  // album view, so they all share albumId/rootAlbumId).
  albumId: string;
  rootAlbumId: string;
  // Selected photo ids the user wants to tag in bulk.
  photoIds: string[];
  // Pre-decrypted tag list of the root, sourced from the parent. Mutated when
  // the user creates a new tag inline.
  availableTags: DecryptedTag[];
  // K_album of the root, needed only to encrypt the name of inline-created tags.
  albumKey: CryptoKey;
  onClose: () => void;
  onTagCreated?: (tag: DecryptedTag) => void;
  // Notify the parent that an attach landed, so the album view can drop any
  // per-photo tag caches it might be holding.
  onApplied?: () => void;
}

// AttachTagsBatchRequest caps at 200 items per backend invariant. We chunk the
// (photo × tag) cartesian product to stay within the cap and to give the user
// progress for very large selections.
const ATTACH_BATCH_MAX = 200;

// Modal action for the multi-select bottom sheet: pick one or more tags and
// apply them to N photos at once. Attach-only — to remove tags in bulk the
// user goes through the per-photo picker (or the future detach-bulk action).
export function BulkTagDialog({
  open,
  albumId,
  rootAlbumId,
  photoIds,
  availableTags,
  albumKey,
  onClose,
  onTagCreated,
  onApplied,
}: Props) {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applyProgress, setApplyProgress] = useState<{ done: number; total: number } | null>(null);

  // Reset transient state every time the dialog opens. Keep selection + filter
  // out of the deps loop on purpose: opening the dialog is the user's signal
  // they want a clean slate.
  useEffect(() => {
    if (!open) return;
    setSelectedTagIds(new Set());
    setNewTagName('');
    setCreateError(null);
    setFilter('');
    setApplyError(null);
    setApplyProgress(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !applying) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, applying]);

  const sortedTags = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return [...availableTags].sort((a, b) => collator.compare(a.name, b.name));
  }, [availableTags]);

  const filteredTags = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sortedTags;
    return sortedTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [sortedTags, filter]);

  if (!open) return null;

  function toggleTag(tagId: string) {
    setSelectedTagIds((curr) => {
      const next = new Set(curr);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const name = newTagName.trim();
    if (!name) {
      setCreateError('inserisci un nome');
      return;
    }
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    if (availableTags.some((t) => collator.compare(t.name, name) === 0)) {
      setCreateError('esiste già un tag con questo nome');
      return;
    }
    setCreating(true);
    try {
      const tagId = crypto.randomUUID();
      const encryptedName = await cv.encryptText(name, albumKey);
      const created = await metadata.createTag({ rootAlbumId, tagId, encryptedName }, session.jwt);
      const decrypted: DecryptedTag = { tag: created, name };
      onTagCreated?.(decrypted);
      setNewTagName('');
      // Auto-select the freshly created tag so the user just hits "Applica"
      // to use it on the current selection.
      setSelectedTagIds((curr) => new Set(curr).add(created.id));
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'errore');
    } finally {
      setCreating(false);
    }
  }

  async function handleApply() {
    if (selectedTagIds.size === 0 || photoIds.length === 0) return;
    setApplying(true);
    setApplyError(null);
    try {
      // Build the cartesian product (photo × tag). attach-batch is idempotent
      // server-side: photos that already have one of the tags get a no-op
      // overwrite of the same row.
      const items: dto.AttachTagsBatchRequest['items'] = [];
      for (const photoId of photoIds) {
        for (const tagId of selectedTagIds) {
          items.push({ tagId, photoId, albumId });
        }
      }
      // Chunk to respect the 200 cap. We do it in series rather than parallel
      // so an early failure doesn't fan out half-applied state.
      const chunks: (typeof items)[] = [];
      for (let i = 0; i < items.length; i += ATTACH_BATCH_MAX) {
        chunks.push(items.slice(i, i + ATTACH_BATCH_MAX));
      }
      setApplyProgress({ done: 0, total: chunks.length });
      for (let i = 0; i < chunks.length; i++) {
        await metadata.attachTagsBatch({ rootAlbumId, items: chunks[i]! }, session.jwt);
        setApplyProgress({ done: i + 1, total: chunks.length });
      }
      onApplied?.();
      onClose();
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'errore');
    } finally {
      setApplying(false);
    }
  }

  const photoLabel = `${photoIds.length} ${photoIds.length === 1 ? 'foto' : 'foto'}`;
  const applyDisabled = applying || creating || selectedTagIds.size === 0 || photoIds.length === 0;
  const applyLabel = applyProgress
    ? `Applico ${applyProgress.done}/${applyProgress.total}…`
    : `Applica${selectedTagIds.size ? ` (${selectedTagIds.size})` : ''}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Applica tag a foto selezionate"
      className="fixed inset-0 z-[110] flex items-end justify-center bg-navy/60 backdrop-blur-sm sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget && !applying) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-3xl border border-outline bg-surface-container p-6 shadow-elevated sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
              <Icon name="sell" className="text-xl" filled />
            </span>
            <div>
              <h2 className="font-display text-lg font-semibold">Applica tag</h2>
              <p className="text-xs text-on-surface-variant">
                Aggiungi tag a {photoLabel} selezionate.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            aria-label="Chiudi"
            className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface disabled:opacity-50"
          >
            <Icon name="close" />
          </button>
        </div>

        <form onSubmit={handleCreate} className="mt-5 flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="Nuovo tag"
              placeholder="es. Mare"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              {...(createError ? { error: createError } : {})}
              disabled={creating || applying}
            />
          </div>
          <Button
            type="submit"
            loading={creating}
            leftIcon={<Icon name="add" />}
            disabled={creating || applying || newTagName.trim().length === 0}
          >
            Crea
          </Button>
        </form>

        {availableTags.length > 4 ? (
          <div className="mt-4">
            <Input
              placeholder="Filtra…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              leftIcon={<Icon name="search" className="text-base" />}
              disabled={applying}
            />
          </div>
        ) : null}

        <div className="mt-4 min-h-[3rem] flex-1 overflow-y-auto">
          {availableTags.length === 0 ? (
            <p className="py-2 text-sm text-on-surface-variant">
              Nessun tag esistente. Creane uno qui sopra.
            </p>
          ) : filteredTags.length === 0 ? (
            <p className="py-2 text-sm text-on-surface-variant">Nessun tag corrisponde.</p>
          ) : (
            <div className="flex flex-wrap gap-2 py-1">
              {filteredTags.map((dt) => (
                <TagChip
                  key={dt.tag.id}
                  name={dt.name}
                  active={selectedTagIds.has(dt.tag.id)}
                  onClick={() => toggleTag(dt.tag.id)}
                />
              ))}
            </div>
          )}
        </div>

        {applyError ? <p className="mt-3 text-sm text-error">Errore: {applyError}</p> : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={applying}>
            Annulla
          </Button>
          <Button onClick={() => void handleApply()} disabled={applyDisabled} loading={applying}>
            {applyLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
